<template>
  <v-app id="app_add_category" style="background-color: #f5f5f5;">
    <v-overlay v-model="centerLoading" :scrim="false" class="custom_centered_overlay">
      <v-progress-circular color="green" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-container fluid class="pa-8">
      <v-row align="center" no-gutters class="mb-6">
        <v-col cols="8">
          <h2 class="text-h5 font-weight-bold mb-1" style="color: #424242; letter-spacing: 0.5px;">CREATE PRODUCT CATEGORY</h2>
          <p class="text-subtitle-2 text-grey-darken-1">Create a new product category according to your requirements.</p>
        </v-col>
        <v-col cols="4" class="d-flex justify-end align-center">
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
            </v-btn>
        </v-col>
      </v-row>

      <v-card class="mx-auto bg-white custom-square-card" elevation="0" border>
        <v-card-title class="px-6 py-4 border-b">
          <span class="text-subtitle-1 font-weight-bold text-grey-darken-2">CREATE NEW PRODUCT CATEGORIES</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-row>
              <v-col cols="12" md="6" class="pr-md-6">
                <div class="mb-6">
                  <v-text-field
                    v-model="mainCategory"
                    label="Main Category *"
                    placeholder="Create main category"
                    variant="outlined"
                    density="comfortable"
                    class="square-border-input"
                    :rules="requiredRule"
                    hide-details="auto"
                  ></v-text-field>
                </div>

                <div class="mb-6">
                  <p class="text-subtitle-2 font-weight-bold mb-2" :class="imageError ? 'text-red' : 'text-grey-darken-3'">Main Category Image <span class="text-black">*</span></p>
                  <div
                    class="image-upload-zone"
                    :class="{ 'has-images': previewImage, 'error-zone': imageError }"
                    @click="triggerFileInput"
                    @drop.prevent="onDrop"
                    @dragover.prevent="onDragOver"
                  >
                    <input ref="fileInput" type="file" hidden accept="image/*" @change="onFileChange" />
                    
                    <div v-if="!previewImage" class="upload-placeholder d-flex flex-column align-center justify-center">
                      <v-icon size="48" :color="imageError ? '#e53935' : '#9fa8da'">mdi-image-plus</v-icon>
                      <div class="mt-4 text-body-2 text-grey-darken-1">
                        Drag your photo here OR <span class="browse-text">Browse from device</span>
                      </div>
                    </div>

                    <div v-else class="w-100 h-100 position-relative pa-2 d-flex justify-center align-center">
                      <v-img :src="previewImage" max-height="200" contain>
                        <div class="delete-overlay">
                          <v-btn icon="mdi-delete" color="red" size="small" @click.stop="removeImage"></v-btn>
                        </div>
                      </v-img>
                    </div>
                  </div>
                </div>

                <div>
                  <div v-for="(sub, index) in subCategories" :key="index" class="d-flex align-center mb-4">
                    <v-text-field
                      v-model="subCategories[index]"
                      label="Sub Category *"
                      placeholder="Create sub category"
                      variant="outlined"
                      density="comfortable"
                      class="square-border-input flex-grow-1 mr-4"
                      :rules="requiredRule"
                      hide-details="auto"
                    ></v-text-field>
                    <v-btn
                      icon="mdi-plus"
                      variant="outlined"
                      class="mr-2 action-btn-add custom-square-card"
                      size="40"
                      @click="addSubCategory"
                    ></v-btn>
                    <v-btn
                      icon="mdi-minus"
                      variant="outlined"
                      class="action-btn-remove custom-square-card"
                      size="40"
                      @click="removeSubCategory(index)"
                      :disabled="subCategories.length === 1"
                    ></v-btn>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="h-100 d-flex flex-column">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <label class="custom-label d-block text-subtitle-2 font-weight-bold" :class="descriptionError ? 'text-red' : 'text-grey-darken-3'">
                      Category Description <span class="text-black">*</span>
                    </label>
                    <span class="text-caption font-weight-bold" :class="charCount >= 300 ? 'text-red' : 'text-grey-darken-1'">
                      {{ charCount }} / 300
                    </span>
                  </div>
                  
                  <v-card flat border class="editor-wrapper custom-square-card flex-grow-1" :class="{ 'error-zone': descriptionError }">
                    <Toolbar :editor="editorRef" :defaultConfig="toolbarConfig" :mode="mode" class="border-b" />
                    <Editor
                      v-model="valueHtml"
                      :defaultConfig="editorConfig"
                      :mode="mode"
                      style="height: 350px; overflow-y: auto;"
                      @onCreated="handleCreated"
                      @onChange="handleChange"
                      @onMaxLength="handleMaxLength"
                    />
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            v-if="permissions.write"
            class="create-btn px-8 custom-square-card"
            height="44"
            elevation="0"
            @click="createCategory"
          >
            CREATE CATEGORY
          </v-btn>
          <v-btn
            variant="outlined"
            class="cancel-btn px-8 ml-4 custom-square-card"
            height="44"
            @click="cancel"
          >
            CANCEL
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" top timeout="3000">
        {{ snackAlert.snackMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import '@wangeditor/editor/dist/css/style.css';
import { i18nChangeLanguage } from '@wangeditor/editor';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import { onBeforeUnmount, ref, shallowRef, onMounted } from 'vue';
import { getPermissions } from "@/plugins/permissions";

i18nChangeLanguage('en');

export default {
  name: 'AddCategory',
  components: { Editor, Toolbar },

  setup() {
    const editorRef = shallowRef();
    const valueHtml = ref('<p></p>');
    const charCount = ref(0);

    onMounted(() => {
      setTimeout(() => { valueHtml.value = '<p></p>'; }, 1500);
    });

    const toolbarConfig = {
      toolbarKeys: ['undo', 'redo', '|', 'bold', 'italic', 'underline', 'through', '|', 'justifyLeft', 'justifyCenter', 'justifyRight', '|', 'bulletedList', 'numberedList', '|', 'insertLink']
    };
    
    // Removed onMaxLength from here to fix the runtime error
    const editorConfig = { 
      placeholder: 'Write a description here...',
      maxLength: 300
    };

    onBeforeUnmount(() => {
      const editor = editorRef.value;
      if (editor) editor.destroy();
    });

    const handleCreated = (editor) => { 
      editorRef.value = editor; 
      charCount.value = editor.getText().replace(/\n/g, '').length;
    };

    const handleChange = (editor) => {
      charCount.value = editor.getText().replace(/\n/g, '').length;
    };

    return { editorRef, mode: 'simple', valueHtml, toolbarConfig, editorConfig, handleCreated, handleChange, charCount };
  },

  data() {
    return {
      permissions: { read: false, write: true, delete: false },
      centerLoading: false,
      mainCategory: '',
      subCategories: [''],
      selectedImageFile: null,
      previewImage: null,
      imageError: false,
      descriptionError: false,
      snackAlert: { snackbar: false, snackColor: "", snackMessage: "", snackBarTop: true },
      requiredRule: [v => !!v || 'This field is required'],
    };
  },

  watch: {
    valueHtml(newVal) {
      const isEmpty = !newVal || newVal.trim() === '<p></p>' || newVal.trim() === '<p><br></p>';
      if (!isEmpty) this.descriptionError = false;
    }
  },

  mounted() {
    try {
      this.permissions = getPermissions(0, "Module Name");
      if (!this.permissions.read) {
        this.showSnackbar("Access Denied", "error");
        this.$router.push('/dashboard');
      }
    } catch (e) {
      console.warn("Permissions plugin missing or failed, proceeding with default access.");
    }
  },
  
  methods: {
    handleMaxLength() {
      // Shows a beautiful warning chip/snackbar when 300 characters are reached
      this.showSnackbar('Maximum character limit (300) reached!', 'warning');
    },

    backPageRoute() {
      this.$router.back();
    },
    
    addSubCategory() {
      this.subCategories.push('');
    },
    
    removeSubCategory(index) {
      if (this.subCategories.length > 1) {
        this.subCategories.splice(index, 1);
      }
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileChange(event) {
      const files = Array.from(event.target.files);
      if (files.length) this.handleFiles(files);
      event.target.value = '';
    },

    onDrop(event) {
      const files = Array.from(event.dataTransfer.files);
      if (files.length) this.handleFiles(files);
    },

    onDragOver(event) {
      event.preventDefault();
    },

    handleFiles(files) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      const file = files[0];
      
      if (!validImageTypes.includes(file.type)) {
         this.showSnackbar('Only image files are allowed!', 'error');
         return;
      }

      this.selectedImageFile = file;
      this.previewImage = URL.createObjectURL(file);
      this.imageError = false;
    },

    removeImage() {
      this.previewImage = null;
      this.selectedImageFile = null;
    },

    showSnackbar(message, color) {
      this.snackAlert.snackMessage = message;
      this.snackAlert.snackColor = color;
      this.snackAlert.snackbar = true;
    },

    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        if (file instanceof Blob) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        } else {
          reject(new Error('Invalid file type'));
        }
      });
    },

    async validateForm() {
      const { valid } = await this.$refs.form.validate();
      let isValid = valid;

      if (!this.selectedImageFile) {
        this.imageError = true;
        isValid = false;
      } else {
        this.imageError = false;
      }

      const isEmptyDesc = !this.valueHtml || this.valueHtml.trim() === '<p></p>' || this.valueHtml.trim() === '<p><br></p>';
      
      if (isEmptyDesc || this.charCount > 300) {
        this.descriptionError = true;
        isValid = false;
        if (this.charCount > 300) {
          this.showSnackbar('Description cannot exceed 300 characters', 'error');
        }
      } else {
        this.descriptionError = false;
      }

      if (!isValid && this.charCount <= 300) {
        this.showSnackbar('Please fill all required fields.', 'error');
      }

      return isValid;
    },

    async createCategory() {
      const isValid = await this.validateForm();
      if (!isValid) return;
      
      this.centerLoading = true;
      const userId = localStorage.getItem('userId') || '1';
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('mainCatagary', this.mainCategory);
      
      const descText = this.valueHtml === '<p></p>' ? '' : this.valueHtml;
      formData.append('mainCatagaryDescription', descText);
      
      const formattedSubCategories = this.subCategories
        .filter(sub => sub.trim() !== '')
        .map(sub => ({ subcatagaryName: sub }));
      formData.append('subCatagary', JSON.stringify(formattedSubCategories));
      
      try {
        if (this.selectedImageFile) {
          const base64Image = await this.convertToBase64(this.selectedImageFile);
          formData.append('productImage', base64Image);
        }

        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/api/addCatagary`;
        const response = await axios.post(apiUrl, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data && response.data.success || response.status === 200 || response.status === 201) {
          this.showSnackbar('Category created successfully!', 'success');
          setTimeout(() => { window.location.reload(); }, 1000);
        } else {
          this.showSnackbar('Failed to create category.', 'error');
        }
      } catch (error) {
        this.showSnackbar('Failed to create category.', 'error');
        console.error('Error creating product:', error);
      } finally {
        this.centerLoading = false;
      }
    },

    cancel() {
      this.mainCategory = '';
      this.subCategories = [''];
      this.valueHtml = '<p></p>';
      this.charCount = 0;
      this.removeImage();
      this.imageError = false;
      this.descriptionError = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
      window.location.reload();
    }
  }
};
</script>

<style scoped>
.custom-square-card { border-radius: 2px !important; }

.square-border-input :deep(.v-field),
.square-border-input :deep(.v-field__outline),
.square-border-input :deep(.v-field__overlay),
.square-border-input :deep(.v-field__outline__start),
.square-border-input :deep(.v-field__outline__end),
.square-border-input :deep(.v-field__outline__notch::before),
.square-border-input :deep(.v-field__outline__notch::after) {
  border-radius: 2px !important;
}

:deep(.v-field__outline) { --v-field-border-opacity: 0.25; }
:deep(.v-input:not(.v-input--error) .v-field--focused .v-field__outline) { --v-field-border-opacity: 1; color: #000000 !important; }
:deep(.v-input:not(.v-input--error) .square-border-input .v-field__outline) { color: #757575 !important; --v-field-border-opacity: 0.4 !important; }
:deep(.v-input--error .v-field__outline) { color: red !important; --v-field-border-opacity: 1 !important; }
:deep(.v-input--error .v-label) { color: red !important; opacity: 1 !important; }
:deep(.v-input--error .v-field__input),
:deep(.v-input--error .v-select__selection-text),
:deep(.v-input--error input) { color: red !important; }
:deep(.v-input--error .v-icon) { color: red !important; }

.editor-wrapper { overflow: hidden; background: #fff; border: 1px solid #e0e0e0 !important; border-radius: 2px; }
.create-btn { background-color: #800080; color: white; font-weight: 700 !important; letter-spacing: 0.5px; }
.cancel-btn { border-color: #bdbdbd !important; color: #616161 !important; font-weight: 700 !important; letter-spacing: 0.5px; }
.action-btn-add { background-color: #e8f5e9 !important; border-color: #81c784 !important; color: #4caf50 !important; }
.action-btn-remove { background-color: #ffffff !important; border-color: #e0e0e0 !important; color: #9e9e9e !important; }

.image-upload-zone { border: 1px dashed #e0e0e0; border-radius: 2px; min-height: 220px; display: flex; align-items: center; justify-content: center; cursor: pointer; background-color: #f8f9fa; transition: all 0.3s ease; overflow: hidden; }
.image-upload-zone:hover { border-color: #1976D2; background-color: #f4f8fc; }
.image-upload-zone.has-images { background-color: white; border: 1px solid #e0e0e0; }

.upload-placeholder { width: 100%; height: 100%; }
.browse-text { color: #1976D2; font-weight: 600; }
.delete-overlay { position: absolute; top: 8px; right: 8px; background: rgba(255, 255, 255, 0.9); border-radius: 2px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }

.error-zone { border: 1px solid #e53935 !important; background-color: #fdecea !important; }
.text-red { color: #e53935 !important; }
</style>